import {
  applyToPoints,
  compose,
  rotate,
  rotateDEG,
  translate,
} from 'transformation-matrix';
import { isEqual, round } from 'lodash-es';

import { Bezier } from 'bezier-js';
import { Units } from '@app/models/dimensions';
import { tunnelDefinitions } from './tunnelDefinitions';

export interface IObstacleLocation {
  x: number;
  y: number;
  r: number;
}

export type ObstacleType =
  | SingleEndedObstacleType
  | DoubleEndedObstacleType
  | TripleEndedObstacleType;

export class Obstacle {
  type: ObstacleType;
  name: string;
  icon: string;

  constructor({
    type,
    name,
    icon,
  }: {
    type: ObstacleType;
    name: string;
    icon: string;
  }) {
    this.type = type;
    this.name = name;
    this.icon = icon;
  }
}

export type IObstacleType =
  | SingleEndedObstacleType
  | DoubleEndedObstacleType
  | TripleEndedObstacleType;

export type SingleEndedObstacleType =
  | 'barrel'
  | 'brushjump'
  | 'hoop'
  | 'logjump'
  | 'longjump'
  | 'nonwingeddoublejump'
  | 'nonwingedonejump'
  | 'nonwingedsinglejump'
  | 'nonwingedtriplejump'
  | 'paneljump'
  | 'pausebox'
  | 'picketfencejump'
  | 'table'
  | 'tirejump'
  | 'walljump'
  | 'waterjump'
  | 'windowjump'
  | 'wingeddoublejump'
  | 'wingedonejump'
  | 'wingedsinglejump'
  | 'wingedtriplejump'
  | 'wishingwelljump'
  | 'other';

export class SingleEndedObstacle extends Obstacle {
  declare type: SingleEndedObstacleType;

  constructor({
    type,
    name,
    icon,
  }: {
    type: SingleEndedObstacleType;
    name: string;
    icon: string;
  }) {
    super({ type, name, icon });
  }
}

export class SingleEndedObstacleLocation implements IObstacleLocation {
  x: number;
  y: number;
  r: number;
  type: SingleEndedObstacleType;

  constructor({
    x,
    y,
    r,
    type,
    start = null,
    end = null,
  }: {
    x?: number;
    y?: number;
    r?: number;
    type?: SingleEndedObstacleType;
    start?: PointObjectNotation | null;
    end?: PointObjectNotation | null;
  }) {
    if (start && end && type) {
      // constructing from user clicked points along the approach edge of the obstacle
      x = round((start.x + end.x) / 2, 1);
      y = round((start.y + end.y) / 2, 1);

      // for single ended obstacles the points are orthogonal to the obstacle orientation
      let rotation =
        Math.atan2(end.y - start.y, end.x - start.x) * (180 / Math.PI) - 90;
      if (rotation < 0) {
        rotation = 360 + rotation;
      }
      if (isSymmetricObstacle(type) && rotation > 180) {
        // rotation is never negative or greater than 180
        rotation = rotation - 180;
      }
      // round rotation
      r = Math.round(rotation % 360);
      if (r === 360) {
        r = 0;
      }
    }
    this.x = x || 0;
    this.y = y || 0;
    this.r = r || 0;
    this.type = type;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  points(design = false): PointObjectNotation[] {
    return [{ x: this.x, y: this.y }];
  }

  hasPoint(point: PointObjectNotation): boolean {
    return this.x === point.x && this.y === point.y;
  }

  updateLocation(location: IObstacleLocation): SingleEndedObstacleLocation {
    if (isEqual({ x: this.x, y: this.y, r: this.r }, location)) {
      return this;
    }

    this.x = location.x;
    this.y = location.y;
    this.r = location.r;

    return this;
  }

  toObject(): object {
    return {
      x: this.x,
      y: this.y,
      r: this.r,
      type: this.type,
    };
  }
}

export type DoubleEndedObstacleType =
  | 'hooptunnel'
  | 'weavepoles3'
  | 'weavepoles6'
  | 'weavepoles9'
  | 'weavepoles12'
  | 'aframe'
  | 'dogwalk'
  | 'teeter'
  | 'platformjump'
  | 'swaybridge'
  | 'swingplank';

export class DoubleEndedObstacle extends Obstacle {
  declare type: DoubleEndedObstacleType;

  constructor({
    type,
    name,
    icon,
  }: {
    type: DoubleEndedObstacleType;
    name: string;
    icon: string;
  }) {
    super({ type, name, icon });
  }
}

const adjustStartEndPoints = (x, y, r, length) => {
  const distance = length / 2;
  const start = {
    x: x - distance * Math.cos(r * (Math.PI / 180)),
    y: y - distance * Math.sin(r * (Math.PI / 180)),
  };
  const end = {
    x: x + distance * Math.cos(r * (Math.PI / 180)),
    y: y + distance * Math.sin(r * (Math.PI / 180)),
  };
  return [start, end];
};

export class DoubleEndedObstacleLocation implements IObstacleLocation {
  x: number;
  y: number;
  r: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  type: DoubleEndedObstacleType;

  constructor({
    x,
    y,
    r,
    x1,
    y1,
    x2,
    y2,
    type,
    start = null,
    end = null,
    units = null,
  }: {
    x?: number;
    y?: number;
    r?: number;
    x1?: number;
    y1?: number;
    x2?: number;
    y2?: number;
    type?: DoubleEndedObstacleType;
    start?: PointObjectNotation | null;
    end?: PointObjectNotation | null;
    units?: Units | null;
  }) {
    let rotation = r || 0;
    if (
      (start !== null && end !== null && type !== null) ||
      (x !== undefined && y !== undefined && r !== undefined)
    ) {
      if (start !== null && end !== null && type !== null) {
        x1 = start.x;
        y1 = start.y;
        x2 = end.x;
        y2 = end.y;

        // start and end are the points inline with the obstacle/dog's approach
        x = round((start.x + end.x) / 2, 1);
        y = round((start.y + end.y) / 2, 1);

        rotation =
          Math.atan2(end.y - start.y, end.x - start.x) * (180 / Math.PI);
      }
      if (rotation < 0) {
        rotation = 360 + rotation;
      }
      // most of the double ended obstacles are symmetric
      if (isSymmetricObstacle(type) && rotation > 180) {
        // rotation is never negative or greater than 180
        rotation = rotation - 180;
      }
      // round rotation
      r = Math.round(rotation % 360);
      if (r === 360) {
        r = 0;
      }

      // adjust start and end points to the correct distance from the center based on the obstacle length and rotation
      switch (type) {
        case 'aframe':
          [{ x: x1, y: y1 }, { x: x2, y: y2 }] = adjustStartEndPoints(
            x,
            y,
            r,
            units === Units.feet ? 14.17 : 4.32,
          );
          break;
        case 'dogwalk':
          [{ x: x1, y: y1 }, { x: x2, y: y2 }] = adjustStartEndPoints(
            x,
            y,
            r,
            units === Units.feet ? 34.67 : 10.57,
          );
          break;
        case 'teeter':
          [{ x: x1, y: y1 }, { x: x2, y: y2 }] = adjustStartEndPoints(
            x,
            y,
            r,
            units === Units.feet ? 11.3 : 3.44,
          );
          break;
        case 'weavepoles3':
          [{ x: x1, y: y1 }, { x: x2, y: y2 }] = adjustStartEndPoints(
            x,
            y,
            r,
            units === Units.feet ? (3 - 1) * 2 : 1.2,
          );
          break;
        case 'weavepoles6':
          [{ x: x1, y: y1 }, { x: x2, y: y2 }] = adjustStartEndPoints(
            x,
            y,
            r,
            units === Units.feet ? (6 - 1) * 2 : 3.0,
          );
          break;
        case 'weavepoles9':
          [{ x: x1, y: y1 }, { x: x2, y: y2 }] = adjustStartEndPoints(
            x,
            y,
            r,
            units === Units.feet ? (9 - 1) * 2 : 4.9,
          );
          break;
        case 'weavepoles12':
          [{ x: x1, y: y1 }, { x: x2, y: y2 }] = adjustStartEndPoints(
            x,
            y,
            r,
            units === Units.feet ? (12 - 1) * 2 : 6.7,
          );
      }
    }
    this.x = x || 0;
    this.y = y || 0;
    this.r = r || 0;
    this.x1 = x1 || 0;
    this.y1 = y1 || 0;
    this.x2 = x2 || 0;
    this.y2 = y2 || 0;
    this.type = type;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  points(design = false): PointObjectNotation[] {
    return [
      { x: this.x1, y: this.y1 },
      { x: this.x2, y: this.y2 },
    ];
  }

  hasPoint(point: PointObjectNotation): boolean {
    return (
      (this.x1 === point.x && this.y1 === point.y) ||
      (this.x2 === point.x && this.y2 === point.y)
    );
  }

  updateLocation(location: IObstacleLocation): DoubleEndedObstacleLocation {
    if (isEqual({ x: this.x, y: this.y, r: this.r }, location)) {
      return this;
    }

    const [{ x: x1, y: y1 }, { x: x2, y: y2 }] = calculateEndpoints(
      this,
      location as DoubleEndedObstacleLocation,
    );
    this.x = location.x;
    this.y = location.y;
    this.r = location.r;
    this.x1 = x1;
    this.y1 = y1;
    this.x2 = x2;
    this.y2 = y2;

    return this;
  }

  toObject(): object {
    return {
      x: this.x,
      y: this.y,
      r: this.r,
      x1: this.x1,
      y1: this.y1,
      x2: this.x2,
      y2: this.y2,
      type: this.type,
    };
  }
}

export type TripleEndedObstacleType =
  | 'tunnel1'
  | 'tunnel2'
  | 'tunnel3'
  | 'tunnel4'
  | 'tunnel5'
  | 'tunnel6';

export class TripleEndedObstacle extends Obstacle {
  declare type: TripleEndedObstacleType;

  constructor({
    type,
    name,
    icon,
  }: {
    type: TripleEndedObstacleType;
    name: string;
    icon: string;
  }) {
    super({ type, name, icon });
  }
}

export class TripleEndedObstacleLocation implements IObstacleLocation {
  x: number;
  y: number;
  r: number;
  // x1, y1 is the start point and x2, y2 is the end point
  // x3, y3 is the side point
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  x3: number;
  y3: number;
  type: TripleEndedObstacleType;
  svgPath: string;
  controls: PointObjectNotation[];
  shape: number;
  bbox: {
    x: { min: number; max: number; size: number; mid: number };
    y: { min: number; max: number; size: number; mid: number };
  };
  _bezier: Bezier;
  _length: number; // expected length of the tunnel in the specified units

  constructor({
    x,
    y,
    r,
    x1,
    y1,
    x2,
    y2,
    x3,
    y3,
    type,
    start = null,
    end = null,
    side = null,
    units = Units.feet,
    controls = [], // control points for the bezier curve
    shape = -1,
  }: {
    x?: number;
    y?: number;
    r?: number;
    x1?: number;
    y1?: number;
    x2?: number;
    y2?: number;
    x3?: number;
    y3?: number;
    type?: TripleEndedObstacleType;
    start?: PointObjectNotation | null;
    end?: PointObjectNotation | null;
    side?: PointObjectNotation | null;
    units?: Units | null;
    controls?: PointObjectNotation[]; // added v3.0.0
    shape?: number; // added v3.0.0
  }) {
    // tunnel type length is in meters
    this.type = type;
    this._length = TripleEndedObstacleLocation.getTunnelLengthFromType(
      units,
      type,
    );
    if (start && end && side) {
      // start and end are the points inline with the obstacle/dog's approach
      x1 = start.x;
      y1 = start.y;
      x2 = end.x;
      y2 = end.y;
      x3 = side.x;
      y3 = side.y;
    }

    this.x = x || 0;
    this.y = y || 0;
    this.r = r || 0;
    this.x1 = x1 || 0;
    this.y1 = y1 || 0;
    this.x2 = x2 || 0;
    this.y2 = y2 || 0;
    this.x3 = x3 || 0;
    this.y3 = y3 || 0;
    this.shape = shape;

    const unitsToInches = units === Units.meters ? 39.37 : 12;
    if (controls.length) {
      this.controls = controls;
    } else if (!controls.length) {
      // construct the bezier curve from the endpoints and side point
      const points: Point[] = [
        { x: this.x1, y: this.y1 },
        { x: this.x3, y: this.y3 },
        { x: this.x2, y: this.y2 },
      ];

      // draw the curve using global coordinates
      const t = 0.5;
      const initialBezier = Bezier.cubicFromPoints(...points, t);
      const matching = this.matchingCubicBezier(
        units,
        this._length,
        initialBezier,
        shape,
      );
      this.shape = matching.closest.shape;
      const match = matching.match;
      // centroid location and rotation
      // draw a line between matches first and last points
      // set x,y to the midpoints of that line.
      // this aligns svg bbox with the points since svg tunnel
      // is aligned to bottom edge of bbox.
      this.x = (match.points[0].x + match.points[3].x) / 2;
      this.y = (match.points[0].y + match.points[3].y) / 2;
      this.r = Math.round(matching.rotation);

      // points in display scale (feet or meters)
      // relative to map 0,0
      this.x1 = match.points[0].x;
      this.y1 = match.points[0].y;
      this.x2 = match.points[3].x;
      this.y2 = match.points[3].y;
      const x3 = match.compute(0.5);
      this.x3 = x3.x;
      this.y3 = x3.y;

      this.controls = matching.closest.controlPoints;
    }
    if (!this._bezier) {
      // controls are in feet/meters relative to obstacle center
      // convert to inches used to draw the bezier curve in SVG
      const displayControls = this.controls.map((p) => ({
        x: p.x * unitsToInches,
        y: p.y * unitsToInches,
      }));

      this._bezier = new Bezier(displayControls);
      this.bbox = this._bezier.bbox();
    }
    this.bbox = this._bezier.bbox();
    if (this.bbox.x.size === 0) {
      this.bbox.x.size = 1;
    }
    if (this.bbox.y.size === 0) {
      this.bbox.y.size = 1;
    }
    this.svgPath = this._bezier.toSVG();
  }

  static getTunnelLengthFromType(units, type: TripleEndedObstacleType): number {
    let length = parseInt(type.split('tunnel')[1], 10);
    if (units === Units.feet) {
      switch (length) {
        case 1:
          length = 3;
          break;
        case 2:
          length = 5;
          break;
        case 3:
          length = 10;
          break;
        case 4:
          length = 12;
          break;
        case 5:
          length = 15;
          break;
        case 6:
          length = 20;
          break;
      }
    }
    return length;
  }

  matchingCubicBezier(
    units: Units,
    length: number,
    initialBezier: Bezier,
    shape: number,
  ): { rotation: number; closest: TunnelDefinition; match: Bezier } {
    // tunnelDefinitions.ts contains all the allowed tunnel definitions
    // find possible matching tunnel definitions based on length, units, and shape
    // (when changing tunnel shape)
    const matchingUnits = tunnelDefinitions.filter(
      (tunnel) =>
        tunnel.length === length &&
        tunnel.units === units &&
        (shape < 0 || tunnel.shape === shape),
    );
    // find the matching tunnel definition whose start and end control point distance is the closest match
    // to the distance of initialBezier start and end control points
    const startEndDistance = Bezier.getUtils().dist(
      initialBezier.points[0],
      initialBezier.points[3],
    );
    const closestMatch = matchingUnits.reduce(
      (acc, tunnel) => {
        const distance = Math.abs(tunnel.startEndDistance - startEndDistance);
        if (distance < acc.distance) {
          acc.distance = distance;
          acc.tunnel = tunnel;
        }
        return acc;
      },
      { distance: Infinity, tunnel: null },
    );
    const closestShape = new Bezier(closestMatch.tunnel.controlPoints);
    // rotate closestShape to match the initialBezier's start and end control points
    const angle =
      Math.atan2(
        initialBezier.points[3].y - initialBezier.points[0].y,
        initialBezier.points[3].x - initialBezier.points[0].x,
      ) +
      (initialBezier.clockwise ? 1 : 0) * Math.PI;
    const rotated = applyToPoints(rotate(angle), closestShape.points);
    // calculate the translation to match the initialBezier's start and end control points
    const initialBBox = initialBezier.bbox();
    const closestBBox = new Bezier(rotated).bbox();
    const dx = initialBBox.x.mid - closestBBox.x.mid;
    const dy = initialBBox.y.mid - closestBBox.y.mid;
    const transform = translate(dx, dy);
    const match = new Bezier(
      applyToPoints(transform, rotated as PointObjectNotation[]),
    );
    // calculate the angle of the match.points relative to the x-axis
    const rotation = angle * (180 / Math.PI);
    return {
      match,
      rotation,
      closest: closestMatch.tunnel,
    };
  }

  points(design = false): PointObjectNotation[] {
    const start = {
      x: this.x1,
      y: this.y1,
    };
    const middle = {
      x: this.x3,
      y: this.y3,
    };
    const end = {
      x: this.x2,
      y: this.y2,
    };
    if (design) {
      return [start, middle, end];
    }
    return [start, end];
  }

  hasPoint(point: PointObjectNotation): boolean {
    return (
      (this.x1 === point.x && this.y1 === point.y) ||
      (this.x2 === point.x && this.y2 === point.y) ||
      (this.x3 === point.x && this.y3 === point.y)
    );
  }

  updateLocation(location: IObstacleLocation): TripleEndedObstacleLocation {
    if (isEqual({ x: this.x, y: this.y, r: this.r }, location)) {
      return this;
    }
    [
      { x: this.x1, y: this.y1 },
      { x: this.x2, y: this.y2 },
      { x: this.x3, y: this.y3 },
    ] = calculateEndpoints(this, location as TripleEndedObstacleLocation);
    this.x = location.x;
    this.y = location.y;
    this.r = location.r;

    return this;
  }

  toObject(): object {
    return {
      x: this.x,
      y: this.y,
      r: this.r,
      x1: this.x1,
      y1: this.y1,
      x2: this.x2,
      y2: this.y2,
      x3: this.x3,
      y3: this.y3,
      controls: this.controls, // added v3.0.0
      shape: this.shape, // added v3.0.0
      type: this.type,
    };
  }

  boundingBox() {
    // this is in the map's coordinate system not inches
    const bbox = this._bezier.bbox();
    return {
      x1: bbox.x.min,
      y1: bbox.y.min,
      x2: bbox.x.max,
      y2: bbox.y.max,
      width: bbox.x.size,
      height: bbox.y.size,
    };
  }
}

export interface Point {
  x: number;
  y: number;
}

export interface DisplayPoint extends Point {
  dx: number;
  dy: number;
}

export type ObstacleLocation =
  | SingleEndedObstacleLocation
  | DoubleEndedObstacleLocation
  | TripleEndedObstacleLocation;

export class ObstacleCategory {
  name: 'Jump' | 'Contact' | 'Tunnel' | 'Weave' | 'Other';
  plural: 'Jumps' | 'Contacts' | 'Tunnels' | 'Weaves' | 'Other';
}

export class ObstacleOption {
  type: IObstacleType;
  name: string;
  namePlural: string;
  icon: string;
  category: ObstacleCategory['name'];
  categoryPlural: ObstacleCategory['plural'];

  constructor({
    type,
    name,
    icon,
    category,
    categoryPlural,
  }: {
    type: IObstacleType;
    name: string;
    icon: string;
    category: ObstacleCategory['name'];
    categoryPlural: ObstacleCategory['plural'];
  }) {
    this.type = type;
    this.name = name;
    this.namePlural =
      name.includes('Tunnel') || name.includes('Weave') ? name : name + 's';
    this.icon = icon;
    this.category = category;
    this.categoryPlural = categoryPlural;
  }
}

export class SingleEndedObstacleOption extends ObstacleOption {
  constructor({
    type,
    name,
    icon,
    category,
    categoryPlural,
  }: {
    type: SingleEndedObstacleType;
    name: string;
    icon: string;
    category: ObstacleCategory['name'];
    categoryPlural: ObstacleCategory['plural'];
  }) {
    super({ type, name, icon, category, categoryPlural });
  }
}

export class DoubleEndedObstacleOption extends ObstacleOption {
  constructor({
    type,
    name,
    icon,
    category,
    categoryPlural,
  }: {
    type: DoubleEndedObstacleType;
    name: string;
    icon: string;
    category: ObstacleCategory['name'];
    categoryPlural: ObstacleCategory['plural'];
  }) {
    super({ type, name, icon, category, categoryPlural });
  }
}

export class TripleEndedObstacleOption extends ObstacleOption {
  constructor({
    type,
    name,
    icon,
    category,
    categoryPlural,
  }: {
    type: TripleEndedObstacleType;
    name: string;
    icon: string;
    category: ObstacleCategory['name'];
    categoryPlural: ObstacleCategory['plural'];
  }) {
    super({ type, name, icon, category, categoryPlural });
  }
}

export const TUNNEL_OPTIONS: Array<ObstacleOption> = [
  new TripleEndedObstacleOption({
    type: 'tunnel1',
    name: "Tunnel 3' 1 m",
    icon: 'assets/tunnels/tunnel1_0.svg',
    category: 'Tunnel',
    categoryPlural: 'Tunnels',
  }),
  new TripleEndedObstacleOption({
    type: 'tunnel2',
    name: "Tunnel 5' 2 m",
    icon: 'assets/tunnels/tunnel2_0.svg',
    category: 'Tunnel',
    categoryPlural: 'Tunnels',
  }),
  new TripleEndedObstacleOption({
    type: 'tunnel3',
    name: "Tunnel 10' 3 m",
    icon: 'assets/tunnels/tunnel3_0.svg',
    category: 'Tunnel',
    categoryPlural: 'Tunnels',
  }),
  new TripleEndedObstacleOption({
    type: 'tunnel4',
    name: "Tunnel 12' 4 m",
    icon: 'assets/tunnels/tunnel4_0.svg',
    category: 'Tunnel',
    categoryPlural: 'Tunnels',
  }),
  new TripleEndedObstacleOption({
    type: 'tunnel5',
    name: "Tunnel 15' 5 m",
    icon: 'assets/tunnels/tunnel5_0.svg',
    category: 'Tunnel',
    categoryPlural: 'Tunnels',
  }),
  new TripleEndedObstacleOption({
    type: 'tunnel6',
    name: "Tunnel 20' 6 m",
    icon: 'assets/tunnels/tunnel6_0.svg',
    category: 'Tunnel',
    categoryPlural: 'Tunnels',
  }),
];

export const WEAVE_OPTIONS: Array<ObstacleOption> = [
  new DoubleEndedObstacleOption({
    type: 'weavepoles3',
    name: '3 Weave Poles',
    icon: 'assets/designer/6WeavePoles.svg',
    category: 'Weave',
    categoryPlural: 'Weaves',
  }),
  new DoubleEndedObstacleOption({
    type: 'weavepoles6',
    name: '6 Weave Poles',
    icon: 'assets/designer/6WeavePoles.svg',
    category: 'Weave',
    categoryPlural: 'Weaves',
  }),
  new DoubleEndedObstacleOption({
    type: 'weavepoles9',
    name: '9 Weave Poles',
    icon: 'assets/designer/6WeavePoles.svg',
    category: 'Weave',
    categoryPlural: 'Weaves',
  }),
  new DoubleEndedObstacleOption({
    type: 'weavepoles12',
    name: '12 Weave Poles',
    icon: 'assets/designer/12WeavePoles.svg',
    category: 'Weave',
    categoryPlural: 'Weaves',
  }),
];

export const OBSTACLE_OPTIONS: Array<ObstacleOption> = [
  new DoubleEndedObstacleOption({
    type: 'aframe',
    name: 'A Frame',
    icon: 'assets/designer/AFrameAKC9Feet.svg',
    category: 'Contact',
    categoryPlural: 'Contacts',
  }),
  new DoubleEndedObstacleOption({
    type: 'dogwalk',
    name: 'Dog Walk',
    icon: 'assets/designer/DogWalk36Feet.svg',
    category: 'Contact',
    categoryPlural: 'Contacts',
  }),
  new DoubleEndedObstacleOption({
    type: 'teeter',
    name: 'Teeter',
    icon: 'assets/designer/Teeter.svg',
    category: 'Contact',
    categoryPlural: 'Contacts',
  }),

  new SingleEndedObstacleOption({
    type: 'wingedsinglejump',
    name: 'Winged Jump',
    icon: 'assets/designer/WingedSingleJump.svg',
    category: 'Jump',
    categoryPlural: 'Jumps',
  }),
  new SingleEndedObstacleOption({
    type: 'wingeddoublejump',
    name: 'Winged Double Jump',
    icon: 'assets/designer/WingedDoubleJump.svg',
    category: 'Jump',
    categoryPlural: 'Jumps',
  }),
  new SingleEndedObstacleOption({
    type: 'wingedtriplejump',
    name: 'Winged Triple Jump',
    icon: 'assets/designer/WingedTripleJump.svg',
    category: 'Jump',
    categoryPlural: 'Jumps',
  }),

  new SingleEndedObstacleOption({
    type: 'nonwingedsinglejump',
    name: 'Non-Winged Jump',
    icon: 'assets/designer/NonWingedSingleJump.svg',
    category: 'Jump',
    categoryPlural: 'Jumps',
  }),
  new SingleEndedObstacleOption({
    type: 'nonwingeddoublejump',
    name: 'Non-Winged Double Jump',
    icon: 'assets/designer/NonWingedDoubleJump.svg',
    category: 'Jump',
    categoryPlural: 'Jumps',
  }),
  new SingleEndedObstacleOption({
    type: 'nonwingedtriplejump',
    name: 'Non-Winged Triple Jump',
    icon: 'assets/designer/NonWingedTripleJump.svg',
    category: 'Jump',
    categoryPlural: 'Jumps',
  }),

  new SingleEndedObstacleOption({
    type: 'paneljump',
    name: 'Panel Jump',
    icon: 'assets/designer/PanelJump.svg',
    category: 'Jump',
    categoryPlural: 'Jumps',
  }),
  new SingleEndedObstacleOption({
    type: 'longjump',
    name: 'Long Jump',
    icon: 'assets/designer/LongJump.svg',
    category: 'Jump',
    categoryPlural: 'Jumps',
  }),
  new SingleEndedObstacleOption({
    type: 'tirejump',
    name: 'Tire Jump',
    icon: 'assets/designer/TireJump.svg',
    category: 'Jump',
    categoryPlural: 'Jumps',
  }),

  new SingleEndedObstacleOption({
    type: 'walljump',
    name: 'Wall Jump',
    icon: 'assets/designer/WallJump.svg',
    category: 'Jump',
    categoryPlural: 'Jumps',
  }),
  new SingleEndedObstacleOption({
    type: 'hoop',
    name: 'Hoop',
    icon: 'assets/designer/Hoop.svg',
    category: 'Jump',
    categoryPlural: 'Jumps',
  }),
  new SingleEndedObstacleOption({
    type: 'barrel',
    name: 'Barrel',
    icon: 'assets/designer/Barrel.svg',
    category: 'Jump',
    categoryPlural: 'Jumps',
  }),

  new SingleEndedObstacleOption({
    type: 'table',
    name: 'Table',
    icon: 'assets/designer/Table.svg',
    category: 'Contact',
    categoryPlural: 'Contacts',
  }),
  new SingleEndedObstacleOption({
    type: 'nonwingedonejump',
    name: 'Non-Winged One Jump',
    icon: 'assets/designer/NonWingedOneJump.svg',
    category: 'Jump',
    categoryPlural: 'Jumps',
  }),
  new SingleEndedObstacleOption({
    type: 'wingedonejump',
    name: 'Winged One Jump',
    icon: 'assets/designer/WingedOneJump.svg',
    category: 'Jump',
    categoryPlural: 'Jumps',
  }),
];

const ALL_OBSTACLE_OPTIONS = [
  ...TUNNEL_OPTIONS,
  ...WEAVE_OPTIONS,
  ...OBSTACLE_OPTIONS,
];

const OTHER_OBSTACLE_OPTION = new ObstacleOption({
  type: 'other',
  name: 'Other',
  icon: 'assets/designer/Other.svg',
  category: 'Other',
  categoryPlural: 'Other',
});

const OBSTACLE_TYPE_TO_OPTIONS = ALL_OBSTACLE_OPTIONS.reduce((acc, option) => {
  acc[option.type] = option;
  return acc;
}, {});

export function getObstacleOption(type: ObstacleType): ObstacleOption {
  if (type in OBSTACLE_TYPE_TO_OPTIONS) {
    return OBSTACLE_TYPE_TO_OPTIONS[type];
  }
  return OTHER_OBSTACLE_OPTION;
}

export function jsonToObstacleLocation(
  obstacle: ObstacleLocation,
  units: Units,
): ObstacleLocation | null {
  switch (obstacle.type) {
    // triple ended obstacles
    case 'tunnel1':
    case 'tunnel2':
    case 'tunnel3':
    case 'tunnel4':
    case 'tunnel5':
    case 'tunnel6':
      return new TripleEndedObstacleLocation({ ...obstacle, units });
    // double ended obstacles
    case 'aframe':
    case 'dogwalk':
    case 'hooptunnel':
    case 'platformjump':
    case 'swaybridge':
    case 'swingplank':
    case 'teeter':
    case 'weavepoles12':
    case 'weavepoles3':
    case 'weavepoles6':
    case 'weavepoles9':
      return new DoubleEndedObstacleLocation({ ...obstacle, units });
    // centroid based obstacles
    case 'barrel':
    case 'brushjump':
    case 'hoop':
    case 'logjump':
    case 'longjump':
    case 'nonwingeddoublejump':
    case 'nonwingedonejump':
    case 'nonwingedsinglejump':
    case 'nonwingedtriplejump':
    case 'paneljump':
    case 'pausebox':
    case 'picketfencejump':
    case 'table':
    case 'tirejump':
    case 'walljump':
    case 'waterjump':
    case 'windowjump':
    case 'wingeddoublejump':
    case 'wingedonejump':
    case 'wingedsinglejump':
    case 'wingedtriplejump':
    case 'wishingwelljump':
    case 'other':
      return new SingleEndedObstacleLocation({ ...obstacle });
    default:
      // const _check: never = obstacle;
      return null;
  }
}

// this could be a member of the Obstacle class but not sure I want to instantiate an Obstacle just to get the option
export function isSymmetricObstacle(type: IObstacleType): boolean {
  switch (type) {
    // non-symmetric obstacles
    case 'longjump':
    case 'nonwingeddoublejump':
    case 'nonwingedtriplejump':
    case 'teeter':
    case 'waterjump':
    case 'wingeddoublejump':
    case 'wingedtriplejump':
      return false;
    // triple ended obstacles
    case 'tunnel1':
    case 'tunnel2':
    case 'tunnel3':
    case 'tunnel5':
    case 'tunnel6':
    // double ended obstacles
    case 'hooptunnel':
    case 'weavepoles3':
    case 'weavepoles6':
    case 'weavepoles9':
    case 'weavepoles12':
    case 'aframe':
    case 'dogwalk':
    case 'platformjump':
    case 'swaybridge':
    case 'swingplank':
    // centroid based obstacles
    case 'table':
    case 'pausebox':
    case 'brushjump':
    case 'logjump':
    case 'nonwingedonejump':
    case 'nonwingedsinglejump':
    case 'paneljump':
    case 'picketfencejump':
    case 'tirejump':
    case 'walljump':
    case 'windowjump':
    case 'wingedonejump':
    case 'wingedsinglejump':
    case 'wishingwelljump':
    case 'other':
      return true;
    default:
      // const _check: never = type;
      return false;
  }
}

export function obstacleLocationCategoryCount(
  obstacleLocations: ObstacleLocation[],
): { [key: string]: number } {
  // count the number of obstacles in each category
  return obstacleLocations.reduce((acc, obstacle) => {
    const option = getObstacleOption(obstacle.type);
    acc[option.categoryPlural] = acc[option.categoryPlural] + 1 || 1;
    return acc;
  }, {});
}

export function obstacleLocationNameCount(
  obstacleLocations: ObstacleLocation[],
): { [key: string]: number } {
  // count the number of obstacles of each type
  return obstacleLocations.reduce((acc, obstacle) => {
    const option = getObstacleOption(obstacle.type);
    acc[option.namePlural] = acc[option.namePlural] + 1 || 1;
    return acc;
  }, {});
}

export function annotateObstacleLocation(obstacleLocation: ObstacleLocation): {
  obstacleLocation: ObstacleLocation;
  option: ObstacleOption;
} {
  if (!obstacleLocation) {
    return null;
  }
  const option = getObstacleOption(obstacleLocation.type);
  return {
    obstacleLocation,
    option,
  };
}

export function obstacleLocationsToPoints(
  obstacleLocations: ObstacleLocation[],
  design: boolean,
): PointObjectNotation[] {
  // convert obstacle locations to points in the map's coordinate system
  // flatMap because some obstacles have multiple points
  const points = obstacleLocations.flatMap((obstacle) =>
    obstacle.points(design),
  );
  return points;
}

export function findPointInObstacleList(
  point: PointObjectNotation,
  obstacles: ObstacleLocation[],
) {
  return obstacles.find((o) => o.hasPoint(point));
}

export function calculateEndpoints(
  currentLocation: DoubleEndedObstacleLocation | TripleEndedObstacleLocation,
  newLocation: DoubleEndedObstacleLocation | TripleEndedObstacleLocation,
): PointObjectNotation[] {
  // x1, y1 is the start point, x2, y2 is the end point, x3, y3 is the middle point
  // x,y is the centroid of the obstacle and
  // r is the updated rotation of the obstacle about the centroid in degrees
  if (isEqual(currentLocation, newLocation)) {
    return [
      {
        x: currentLocation.x1,
        y: currentLocation.y1,
      },
      {
        x: currentLocation.x2,
        y: currentLocation.y2,
      },
    ];
  }
  const matrix = compose(
    translate(
      newLocation.x - currentLocation.x,
      newLocation.y - currentLocation.y,
    ),
    rotateDEG(
      newLocation.r - currentLocation.r,
      currentLocation.x,
      currentLocation.y,
    ),
  );
  const points = [
    { x: round(currentLocation.x1, 2), y: round(currentLocation.y1, 2) },
    { x: round(currentLocation.x2, 2), y: round(currentLocation.y2, 2) },
  ];
  if (currentLocation instanceof TripleEndedObstacleLocation) {
    points.push({
      x: round(currentLocation.x3, 2),
      y: round(currentLocation.y3, 2),
    });
  }
  return applyToPoints(matrix, points);
}

export type TunnelDefinition = {
  centroid: Point;
  controlPoints: Point[];
  length: number;
  radius: number;
  shape: number;
  startEndDistance: number;
  svgPath: string;
  svg: string;
  type: TripleEndedObstacleType;
  units: Units;
  url: string;
};

export function typeToCategory(
  type: IObstacleType,
): ObstacleCategory['name'] | null {
  const option = getObstacleOption(type);
  if (option) {
    return option.category;
  }
  return null;
}
